import React, { ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import dayjs from 'dayjs';

import Row from 'components/common/Row';
import Text from 'components/common/Text';
import Column from 'components/common/Column';
import { IconExclamationCircle, ICON_CLOSE_32 } from 'assets/icons';
import {
  ProductParamInput,
  ScheduleParam,
} from 'containers/ProductRegist/hooks/types';
import { InventoryTargetType, ScheduleState } from 'types/api';
import AuthButton from 'components/Button/AuthButton';
import ConfirmDialog from 'components/Modal/ConfirmDialog';
import CalendarSchedule from 'containers/ProductDetail/components/ModifyForm/CalendarSchedule';
import { OptionSheetType } from 'containers/ProductRegist/components/OptionSelectRegist/hooks/useCalenderHooks';
import { useDateAdditionalHooks } from 'containers/ProductRegist/components/OptionSelectRegist/hooks/useDateAdditionalHooks';
import ScheduleStatusButton from 'containers/ProductRegist/components/OptionSelectRegist/components/ScheduleStatusButton';
import OptionSelectFormV2 from 'containers/ProductRegist/components/OptionSelectRegist/components/OptionSelectFormV2';
import OptionTermDateV2 from 'containers/ProductRegist/components/OptionSelectRegist/components/OptionTermDateV2';
import OptionRecruitPeopleV2 from 'containers/ProductRegist/components/OptionSelectRegist/components/OptionRecruitPeopleV2';
import OptionDueDateFormV2 from 'containers/ProductRegist/components/OptionSelectRegist/components/OptionDueDateFormV2';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import CommonImage from 'components/common/CommonImage';

interface Props {
  isMobile: boolean;
  productParamInput: ProductParamInput;
  scheduleItem: ScheduleParam;
  optionSheetType: OptionSheetType;
  onClose: () => void;
  handleSetScheduleItem: (changes: Record<string, any>) => void;
  setCheckedDate: React.Dispatch<React.SetStateAction<any[]>>;
  handleOnChangeProductParamInput: (key: string, value: any) => void;
  handleGetProductScheduleTerm: () => void;
}

const CalendarModifyFormV2 = (props: Props) => {
  const {
    isMobile,
    productParamInput,
    scheduleItem,
    optionSheetType,
    onClose,
    handleSetScheduleItem,
    setCheckedDate,
    handleOnChangeProductParamInput,
    handleGetProductScheduleTerm,
  } = props;

  const {
    isAppliedAllItems,
    isOverDay,
    selectedItems,
    termDate,
    endDateFormat,
    endDueDate,
    isOpenDeleteDialog,
    setTermDate,
    setIsOpenDeleteDialog,
    handleSetIsAppliedAllItems,
    handleAddOptions,
    handleAddAllOptions,
    handleSetOverDay,
    handleSetEndDueDate,
    handleSetEndDateFormat,
  } = useDateAdditionalHooks(scheduleItem, handleSetScheduleItem);

  const isByScheduleOptions =
    productParamInput?.inventoryTargetType === InventoryTargetType.BY_SCHEDULE; // 옵션별 인원 제한(BY_ITEM) or 일정별 인원 제한(BY_SCHEDULE)

  const checkOptions = isAppliedAllItems
    ? true
    : !isEmpty(scheduleItem.itemParamIds);

  const isAbled =
    checkOptions && termDate.startDate !== '' && termDate.endDate !== '';

  const isCanceledSchedule = scheduleItem?.status === ScheduleState.CANCELED;

  const isDisable =
    scheduleItem?.status &&
    (isCanceledSchedule
      ? true
      : scheduleItem.status !== ScheduleState.EDITING &&
        !(optionSheetType === OptionSheetType.CREATE));

  const handleValidCheck = () => {
    if (
      productParamInput.inventoryTargetType !== InventoryTargetType.BY_ITEM &&
      scheduleItem.minimumQuota > scheduleItem.quota
    ) {
      window.showToast('최대 인원은 최소 인원보다 적을 수 없습니다.', 'failed');

      return;
    }

    if (
      productParamInput.inventoryTargetType !== InventoryTargetType.BY_ITEM &&
      (scheduleItem.minimumQuota === 0 || scheduleItem.quota === 0)
    ) {
      window.showToast('최소/최대인원은 0명으로 설정할 수 없어요.', 'failed');

      return;
    }
  };

  /* 캘린더에서 수정 클릭시 작동 */
  const handleUpdateScheduleItems = () => {
    handleValidCheck();

    const updatedItems = productParamInput.frip!.schedules.map(item => item);
    const index = productParamInput.frip!.schedules.findIndex(
      item => item.id === scheduleItem.id,
    );
    const oneDayMs = 24 * 60 * 60 * 1000;

    const startedTime = termDate.startDate.split(':');
    const endedTime = termDate.endDate.split(':');
    const startDate = new Date(updatedItems[index].term.startedAt);
    const endDate = new Date(
      startDate.getTime() + oneDayMs * Number(termDate.durationDate),
    );

    startDate.setHours(Number(startedTime[0]));
    startDate.setMinutes(Number(startedTime[1]));
    endDate.setHours(Number(endedTime[0]));
    endDate.setMinutes(Number(endedTime[1]));

    if (isOverDay) {
      endDate.setTime(
        startDate.getTime() + oneDayMs * Number(termDate.durationDate),
      );
    }

    let applicationEndDate = cloneDeep(startDate);

    if (endDateFormat === '1day') {
      applicationEndDate.setDate(applicationEndDate.getDate() - 1);
    }

    if (endDateFormat === '2day') {
      applicationEndDate.setDate(applicationEndDate.getDate() - 2);
    }

    if (endDateFormat === '3day') {
      applicationEndDate.setDate(applicationEndDate.getDate() - 3);
    }

    if (endDateFormat === 'select') {
      if (endDueDate.date !== 0) {
        applicationEndDate.setDate(
          applicationEndDate.getDate() - endDueDate.date,
        );
      }

      if (endDueDate.time !== 0) {
        applicationEndDate.setHours(
          applicationEndDate.getHours() - endDueDate.time,
        );
      }
    }

    const selectedParamIdList = selectedItems.map(item => item.paramId);

    let applicationStartDate = cloneDeep(applicationEndDate);
    applicationStartDate.setMonth(applicationStartDate.getMonth() - 3);

    updatedItems[index] = {
      ...updatedItems[index],
      appliedAllItems: isAppliedAllItems,
      term: {
        duration: dayjs(endDate).diff(startDate),
        startedAt: startDate.getTime(),
      },
      title: dayjs(startDate).format('YYYY년 M월 D일 HH:mm'),
      saleTerm: {
        startedAt: applicationStartDate.getTime(),
        endedAt: applicationEndDate.getTime(),
      },
      minimumQuota: Number(scheduleItem.minimumQuota),
      quota: scheduleItem.quota,
      itemParamIds: scheduleItem.appliedAllItems
        ? []
        : Array.from(
            new Set([...selectedParamIdList, ...scheduleItem.itemParamIds]),
          ).filter(item => item !== undefined),
    };

    const isDuplicate = productParamInput.frip.schedules
      .filter(
        schedule =>
          !schedule.status ||
          [ScheduleState.EDITING, ScheduleState.OPENED].includes(
            schedule.status,
          ),
      )
      .some(
        schedule =>
          schedule.term.startedAt === startDate.getTime() &&
          schedule.paramId !== updatedItems[index].paramId,
      );

    if (isDuplicate) {
      window.showToast('이미 존재하는 일정이에요.', 'failed');

      return;
    }

    handleOnChangeProductParamInput('frip', {
      ...productParamInput.frip,
      schedules: updatedItems,
    });

    setCheckedDate([]);
    window.showToast('일정이 수정을 완료했어요.', 'success');
    onClose();
  };

  const handleDeleteScheduleItems = () => {
    handleOnChangeProductParamInput('frip', {
      ...productParamInput.frip,
      schedules: productParamInput.frip?.schedules.filter(
        item => item.id !== scheduleItem.id,
      ),
    });

    setCheckedDate([]);
    setIsOpenDeleteDialog(false);
    window.showToast('일정이 수정을 완료했어요.', 'success');
    onClose();
  };

  const renderHeader = (): JSX.Element => {
    return (
      <Row
        justifyContent="space-between"
        style={{
          position: 'sticky',
          top: '0',
          padding: isMobile ? '24px 20px' : '32px 20px ',
          width: '100%',
          background: '#fff',
          zIndex: 3,
        }}
      >
        <Row gap="12px">
          <ScheduleStatusButton
            isMobile={isMobile}
            status={scheduleItem.status}
          />
          <Text
            color="#1b1c1e"
            size={isMobile ? '20px' : '24px'}
            weight={isMobile ? 600 : 500}
            style={{ lineHeight: '26px' }}
          >
            {dayjs(scheduleItem.term.startedAt).format('YY년 M월 D일 HH:mm')}~
            {dayjs(
              scheduleItem.term.startedAt + scheduleItem.term.duration,
            ).format('HH:mm')}
          </Text>
        </Row>
        <Row onClick={onClose}>
          <ICON_CLOSE_32
            color="#70737C"
            style={{
              width: isMobile ? '24px' : 'auto',
              height: isMobile ? '24px' : 'auto',
            }}
          />
        </Row>
      </Row>
    );
  };

  const renderContents = (): JSX.Element => {
    return (
      <Column
        align="flex-start"
        gap="32px"
        style={{ padding: isMobile ? '16px 20px 40px' : '12px 20px 40px' }}
      >
        {scheduleItem.status &&
          scheduleItem.status !== ScheduleState.EDITING && (
            <Row gap="12px">
              <IconExclamationCircle
                color="#0071E9"
                width="24px"
                height="24px"
              />
              <Text color="#0071E9" size="16px">
                {isByScheduleOptions
                  ? '모집 인원만 수정이 가능합니다.'
                  : '판매가 시작된 일정은 옵션, 진행시간, 신청 가능 기간 수정이 불가능해요'}
              </Text>
            </Row>
          )}
        <OptionSelectFormV2
          isMobile={isMobile}
          isAppliedAllItems={isAppliedAllItems}
          productParamInput={productParamInput}
          scheduleItem={scheduleItem}
          isDisable={isDisable}
          handleSetIsAppliedAllItems={handleSetIsAppliedAllItems}
          handleAddOptions={handleAddOptions}
          handleAddAllOptions={handleAddAllOptions}
        />
        <OptionTermDateV2
          isMobile={isMobile}
          isOverDay={isOverDay}
          termDate={termDate}
          isDisable={isDisable}
          setTermDate={setTermDate}
          handleSetOverDay={handleSetOverDay}
        />
        {!(
          productParamInput.frip.attachedToSchedule &&
          productParamInput.inventoryTargetType === InventoryTargetType.BY_ITEM
        ) && (
          <OptionRecruitPeopleV2
            scheduleItem={scheduleItem}
            isDisable={isCanceledSchedule}
            handleSetScheduleItem={handleSetScheduleItem}
          />
        )}

        <OptionDueDateFormV2
          scheduleItem={scheduleItem}
          endDateFormat={endDateFormat}
          endDueDate={endDueDate}
          isDisable={isDisable}
          handleSetEndDueDate={handleSetEndDueDate}
          handleSetEndDateFormat={handleSetEndDateFormat}
        />
      </Column>
    );
  };

  const renderBottomSheet = (): JSX.Element => {
    if (scheduleItem.status === ScheduleState.CANCELED) {
      return;
    }

    return (
      <Row
        justifyContent="flex-end"
        gap="16px"
        style={{
          position: 'sticky',
          bottom: '0',
          padding: '16px 20px 32px',
          width: '100%',
          background: '#fff',
          borderTop: '1px solid #Eaebec',
        }}
      >
        <CalendarSchedule
          scheduleItem={scheduleItem}
          handleGetProductScheduleTerm={handleGetProductScheduleTerm}
          onClose={onClose}
        />
        {scheduleItem.status === ScheduleState.EDITING && (
          <AuthButton
            name="일정 삭제하기"
            isAbled={true}
            onClick={() => setIsOpenDeleteDialog(true)}
            style={{
              padding: '12px 24px',
              width: 'auto',
              minWidth: '76px',
              borderRadius: '12px',
              background: '#FFF0F0',
              color: '#FF4242',
            }}
          />
        )}
        {isByScheduleOptions ? (
          <AuthButton
            name="수정"
            isAbled={isAbled}
            onClick={handleUpdateScheduleItems}
            style={{
              padding: '12px 24px',
              width: 'auto',
              minWidth: '76px',
              borderRadius: '12px',
            }}
          />
        ) : (
          <></>
        )}
      </Row>
    );
  };

  return (
    <>
      <ModalContainer
        isMobile={isMobile}
        isAbled={true}
        isBottomSheet
        onCloseText=" "
        onAcceptText="완료"
        onClose={() => {}}
        onClick={() => {}}
        style={{ gap: '0' }}
      >
        {renderHeader()}
        {renderContents()}
        {renderBottomSheet()}
      </ModalContainer>
      {isOpenDeleteDialog && (
        <ConfirmDialog
          title="선택한 일정을 삭제할까요?"
          description="일정은 저장되지 않고 삭제됩니다."
          onCloseText="아니요"
          onClose={() => {
            setIsOpenDeleteDialog(false);
          }}
          onAcceptText="네, 삭제할게요"
          onAccept={handleDeleteScheduleItems}
        />
      )}
    </>
  );
};

export default CalendarModifyFormV2;
